import {useEffect, useRef} from 'react'
import {getFirstInvalidField, useFormContext} from '../../integrations'
import {useTabsContext} from '@chakra-ui/react'
import {useStore} from '@tanstack/react-form'

/**
 * A form component that automatically focuses and navigates to the first invalid field
 * when a form is submitted with validation errors.
 *
 * Works both inside and outside of tab contexts:
 * - Inside `<Tabs.Root>`: switches to the tab containing the first error, then focuses it
 * - Outside `<Tabs.Root>`: focuses the first invalid field directly
 *
 * Handles two cases:
 * 1. Fields that become invalid (MutationObserver) — catches fields whose `aria-invalid`
 *    attribute changes to "true" in the DOM
 * 2. Already-invalid fields on submit (submit event listener) — catches fields that were
 *    already invalid from a previous submission and re-focused on the next submit
 *
 * Usage:
 *   <Tabs.Root>
 *     <form.ErrorObserver />
 *     <Tabs.List>...</Tabs.List>
 *     <Tabs.Content>...</Tabs.Content>
 *   </Tabs.Root>
 */

function getTabValue(element: HTMLElement): string | undefined {
  const tabPanel = element.closest('[role="tabpanel"]') as HTMLElement
  if (!tabPanel) return undefined
  return tabPanel.getAttribute('aria-labelledby')?.split('trigger-')[1]
}

function isInvalidField(mutation: MutationRecord): HTMLElement | null {
  if (mutation.type !== 'attributes') return null
  if (mutation.attributeName !== 'aria-invalid') return null

  const target = mutation.target as HTMLElement
  if (target.getAttribute('aria-invalid') !== 'true') return null

  return target
}

function focusInvalidField(
  element: HTMLElement | undefined | null,
  tabsContext: ReturnType<typeof useTabsContext> | undefined,
  focusingRef: React.RefObject<boolean>
) {
  if (!element || focusingRef.current) return

  if (!tabsContext) {
    focusingRef.current = true
    element.focus()
    return
  }

  const tabValue = getTabValue(element)
  if (!tabValue) return

  focusingRef.current = true
  tabsContext.setValue(tabValue)
  requestAnimationFrame(() => {
    element.focus()
  })
}

export function ErrorObserver() {
  let tabsContext: ReturnType<typeof useTabsContext> | undefined
  let hasTabs = false
  const form = useFormContext()

  try {
    tabsContext = useTabsContext()
    hasTabs = true
  } catch {}

  const anchorRef = useRef<HTMLSpanElement>(null)
  const focusingRef = useRef(false)

  useEffect(() => {
    const handleMutation = (mutation: MutationRecord) => {
      const invalidField = isInvalidField(mutation)
      focusInvalidField(invalidField, tabsContext, focusingRef)
    }

    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        handleMutation(mutation)
      }

      focusingRef.current = false
    })

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['aria-invalid'],
      subtree: true,
    })

    const handleSubmit = (event: Event) => {
      const selfParent = anchorRef.current?.closest('form')
      if (event.target !== selfParent) {
        return
      }

      const target = getFirstInvalidField()
      focusInvalidField(target, tabsContext, focusingRef)
      focusingRef.current = false
    }

    document.addEventListener('submit', handleSubmit, true)

    return () => {
      observer.disconnect()
      document.removeEventListener('submit', handleSubmit, true)
    }
  }, [hasTabs, tabsContext?.value])

  const errors = useStore(form.store, state => state.errors)
  useEffect(() => {
    if (errors.length) {
      console.group('Form errors')
      console.log(errors)
      console.groupEnd()
    }
  }, [errors])

  return <span ref={anchorRef} hidden data-role="error-observer" />
}
